import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, FolderMinus } from 'lucide-react';
import Modal from '../components/Modal';
import './FormPage.css';
import { API_BASE_URL } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function CostCenters() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [centers, setCenters] = useState([]);
  const [formData, setFormData] = useState({ id: null, cc_nome: '', cc_codigo: '' });

  useEffect(() => {
    fetchCenters();
  }, []);

  const fetchCenters = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/centro_custos.php`);
      const data = await response.json();
      setCenters(data);
    } catch (error) {
      console.error('Erro ao buscar centros de custo:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = formData.id ? 'PUT' : 'POST';
    
    try {
      const payload = {
        ...formData
      };

      const response = await fetch(`${API_BASE_URL}/centro_custos.php`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchCenters();
      } else {
        showAlert('Aviso','Erro ao salvar centro de custo');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const handleEdit = (center) => {
    setFormData({ 
      id: center.id, 
      cc_nome: center.cc_nome, 
      cc_codigo: center.cc_codigo
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm('Confirmação', 'Tem certeza que deseja excluir este centro de custo?');
    if (!confirmed) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/centro_custos.php?id=${id}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        fetchCenters();
      } else {
        showAlert('Aviso','Erro ao excluir centro de custo');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const openNewModal = () => {
    setFormData({ id: null, cc_nome: '', cc_codigo: '' });
    setIsModalOpen(true);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Centro de Custos</h1>
          <p className="page-subtitle">Estruture e acompanhe orçamentos por departamento</p>
        </div>
        <button className="btn-primary" onClick={openNewModal} style={{ width: 'auto', padding: '0.75rem 1.25rem' }}>
          <Plus size={18} />
          Novo Centro
        </button>
      </div>

      <div className="summary-grid" style={{ marginBottom: '1rem' }}>
        <div className="summary-card card">
           <div className="summary-card-header" style={{ marginBottom: '0'}}>
             <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
               <div className="summary-icon-wrapper bg-primary">
                 <FolderMinus size={24} className="icon-primary" />
               </div>
               <div>
                  <h3 className="summary-title" style={{ marginBottom: '0'}}>Total de Centros</h3>
                  <div className="summary-value" style={{ fontSize: '1.25rem'}}>{centers.length} ativos</div>
               </div>
             </div>
           </div>
        </div>
      </div>

      <div className="card list-card">
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nome do Centro de Custo</th>

                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {centers.map((center) => (
                <tr key={center.id}>
                  <td><span className="text-muted" style={{fontFamily: 'monospace'}}>{center.cc_codigo}</span></td>
                  <td className="font-medium">{center.cc_nome}</td>

                  <td>
                    <span className="badge badge-success">
                      Ativo
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Editar" onClick={() => handleEdit(center)}><Edit2 size={16} /></button>
                      <button className="btn-icon danger" title="Excluir" onClick={() => handleDelete(center.id)}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {centers.length === 0 && (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                    Nenhum centro de custo registrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={formData.id ? "Editar Centro de Custo" : "Novo Centro de Custo"}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="form-group flex-2">
              <label>Nome do Departamento / Centro</label>
              <input type="text" name="cc_nome" value={formData.cc_nome} onChange={handleInputChange} placeholder="Ex: Pesquisa e Desenvolvimento" required className="standard-input" />
            </div>
            <div className="form-group flex-1">
              <label>Código Interno</label>
              <input type="text" name="cc_codigo" value={formData.cc_codigo} onChange={handleInputChange} placeholder="Ex: CC-PD-05" required className="standard-input" />
            </div>
          </div>
          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
            <button type="button" className="btn-outline" onClick={() => setIsModalOpen(false)}>Cancelar</button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0.6rem 1.25rem' }}>Salvar Centro</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
